import { Injectable } from "@angular/core";
import { isOpenableLinkUrl } from "@loader-event-bus/open-link.model";
import { AppParentService } from "../core";

/** The mouse button reported by `auxclick` for the middle button. */
const MIDDLE_BUTTON = 1;

/**
 * Captures link activations anywhere in the app document and forwards the openable ones
 * to the host page, which opens them with its real origin (a popup opened from the
 * sandboxed iframe would inherit the opaque origin).
 *
 * The interceptor only cancels the default action: it never stops the propagation,
 * so the handlers of the elements containing the link (e.g. bot messages) still run.
 */
@Injectable({ providedIn: "root" })
export class LinkInterceptor {
  private readonly listener = (event: MouseEvent): void => this.onActivation(event);

  constructor(private readonly appParentService: AppParentService) {}

  /**
   * Starts listening to link activations on the passed document.
   * Called once at bootstrap: the document lives as long as the app, so listeners are never removed.
   */
  public start(doc: Document): void {
    doc.addEventListener("click", this.listener, { capture: true });
    doc.addEventListener("auxclick", this.listener, { capture: true });
  }

  private onActivation(event: MouseEvent): void {
    if (event.defaultPrevented) {
      return;
    }
    if (event.type === "auxclick" && event.button !== MIDDLE_BUTTON) {
      return;
    }

    const anchor = event.target instanceof Element ? event.target.closest("a") : null;
    if (
      !(anchor instanceof HTMLAnchorElement) ||
      !anchor.href ||
      anchor.hasAttribute("download") ||
      // `href` is resolved to an absolute http(s) URL, so in-page fragments are detected on the attribute
      anchor.getAttribute("href")?.startsWith("#") ||
      !isOpenableLinkUrl(anchor.href)
    ) {
      return;
    }

    event.preventDefault();
    this.appParentService.notifyOpenLink(anchor.href);
  }
}
